import {
  UniqueVisitor,
  User,
  Visitor,
  IntegrationService,
  OrgIntegrationService,
  LBBContact,
  SESSION_ACTION,
  SESSION_INTERACTION_KIND,
  MinimalUser,
  LBBCompany,
  CallLog,
} from 'sb-lib/types';
import type { WidgetState } from './realtime';
import type { VisitorSessionRecording } from './recording';

export type Client = Visitor;

export interface FilteredClient extends Partial<Client> {
  lastSeen?: number;
}

export interface ActiveVisitor extends Client {
  id: string;
  isLoading: boolean;
  hasLoaded: boolean;
  widgetState?: WidgetState;
  isEditing: boolean;
  contact: LBBContact | null;
  company: LBBCompany | null;
}

export interface CrmContactSearchResult {
  email: string;
  services: OrgIntegrationService[];
  displayName: string;
  company: string;
  avatarUrl: string;
}

export interface CrmContact {
  email: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  company?: string;
  companyName?: string;
  companyDomain?: string;
  companyEmployees?: number;
  createCompany?: boolean;
  visitorId?: string;
  isLead?: boolean;
}

export interface CrmContactCardBlock {
  header: string;
  data: Record<string, string> | null;
  externalUrl: string | null;
}

export interface CrmContactCard {
  title: string;
  service: IntegrationService;
  data: Record<string, string> | null;
  externalUrl: string | null;
  nestedBlocks: CrmContactCardBlock[] | null;
  linkable: boolean;
  creatable: boolean;
}

export interface ClientEdit {
  sbid: string;
  displayName?: string;
  email?: string;
  avatar?: string;
  contactId?: number;
}

export enum TAG_TYPE {
  NEW_PLAN = 'NEW_PLAN',
  TRIAL_PLAN = 'TRIAL_PLAN',
  PAID_PLAN = 'PAID_PLAN',
}

export type UnisearchResults = {
  query: string;
  paths: string[];
  names: string[];
  customIds: string[];
  emails: string[];
  namesTotalCount: number;
  pathsTotalCount: number;
  customIdsTotalCount: number;
  emailsTotalCount: number;
};

export const CONSOLIDATED_ACTIVITY = { ...SESSION_ACTION, ...SESSION_INTERACTION_KIND };
export type CONSOLIDATED_ACTIVITY = SESSION_INTERACTION_KIND | SESSION_ACTION;
export type PageVisit = {
  url: string;
  urlTitle: string;
  duration: number;
  timestamp: number;
};
export type ConsolidatedSession = {
  id: string;
  startTime: number;
  endTime: number;
  duration: number;
  referrer: string;
  pageVisits: PageVisit[];
  activities: CONSOLIDATED_ACTIVITY[];
  isFirst: boolean;
  user: MinimalUser | null;
};

export enum ReplayVisibility {
  ORG = 'ORG',
  ANYONE = 'ANYONE',
}

export interface ReplayPermission {
  visibility: ReplayVisibility;
  dateUpdated: number;
  /** The user id of who last updated this permission. */
  lastUpdatedBy: number;
}

// Similar to `ConsolidatedSession`. Tracking issue for the refactor:
// https://github.com/GetServiceBell/ServiceBell/issues/1882
export interface SessionSummary {
  sessionGroup: string;
  didAlert: boolean;
  didDial: boolean;
  didMissDial: boolean;
  didSpy: boolean;
  didTakeover: boolean;
  didAcceptTakeover: boolean;
  didVideo: boolean;
  didAcceptVideo: boolean;
  rating?: number;
  startTime: number;
  endTime: number;
  users: User[];
  visitor: UniqueVisitor;
  recordings: VisitorSessionRecording[];
  replayPermission?: ReplayPermission;
  callLog?: CallLog;
}

export interface Action {
  kind: 'PAGE_VISIT' | 'ALERT' | 'FEEDBACK' | 'REQUESTED_HELP' | 'HELP_REQUEST_MISSED';
  data?: {
    url?: string;
    urlTitle?: string;
    stars?: number;
  };
}
export interface Interaction {
  kind: 'SPYING' | 'VIDEO' | 'TAKEOVER' | 'DISPLAYSHARE' | 'DISPLAYDEMO';
  acceptTime?: number;
  userId: number;
  userDisplayName: string | null;
}
export type SessionEvent = { timestamp: number } & (Action | Interaction);
export type FeedbackAnswer = {
  id: number;
  questionTitle: null | string;
  questionText: null | string;
  pointCount: number;
  feedbackType: 'POINTS' | 'TEXT';
  points: null | number;
  text: null | string;
};
export interface SessionDetails {
  events: SessionEvent[];
  userAgent: string;
  location: string;
  visitorEmail: any;
  feedback: FeedbackAnswer[];
  conversations: number[];
}

export type SessionSummaryActionFilter =
  | 'did_alert'
  | 'did_dial'
  | 'did_miss_dial'
  | 'did_spy'
  | 'did_takeover'
  | 'did_video';

export enum WidgetStage {
  NEED_HELP = 'NEED_HELP',
  DIALING = 'DIALING',
  ANSWER_CALL = 'ANSWER_CALL',
  ACTIVE_CALL = 'ACTIVE_CALL',
  CALL_ENDED = 'CALL_ENDED',
  BOOK_MEETING = 'BOOK_MEETING',
  MEETING_CALENDAR = 'MEETING_CALENDAR',
  PROACTIVE_VIDEO = 'PROACTIVE_VIDEO',
}

export enum WidgetAvailabilityStatus {
  AVAILABLE = 'AVAILABLE',
  BUSY = 'BUSY',
  AWAY = 'AWAY',
}
